/**
 * Combat FEEDBACK dispatcher — the ONE chokepoint where a confirmed hit becomes feel.
 *
 * Everything cosmetic that sells an impact fires from here, in the order the action-game
 * canon converged on: HITSTOP (freeze the attacker's animation for a few frames) → camera
 * kick → the caller's own VFX/SFX/damage numbers. One object owns the numbers so every
 * new attack gets the whole stack from one call, instead of each call site hand-rolling
 * its own subset with its own constants.
 *
 * HITSTOP freezes ONLY the local avatar's ANIMATION clock (the dt main.ts feeds
 * avatar.update). The GAMEPLAY action clock (avatar.stepAction, fixed tick) keeps running
 * on purpose: the server resolves a swing's whole arc at punch receipt, so freezing the
 * gameplay clock would desync the client's swept prediction from damage the server has
 * already dealt. A 70–150 ms visual freeze over a live sweep is imperceptible — the
 * freeze is the feel, the sweep is the truth.
 *
 * The camera kick is applied AFTER the frame's camera rig has fully posed the camera, in
 * CAMERA-LOCAL axes, so it can never fight the world-space rigs — and it is deliberately
 * skipped under devCamPin (main.ts applies the pin after this), so harness screenshots
 * stay deterministic.
 */
import * as THREE from "three";

/** Freeze lengths (s). Industry reference points: lights 3–6 frames, heavies 8–15,
 *  killing blows read best held slightly longer (Smash scales hitlag with damage). */
const LIGHT_HITSTOP = 0.07;
const HEAVY_HITSTOP = 0.12;
const KILL_HITSTOP = 0.15;
/** Animation runs at this fraction during the freeze — NOT a hard 0, so cloth/IK keep
 *  settling and the held pose reads as impact, not as a hang. */
const FREEZE_SCALE = 0.06;
/** Camera-kick exponential decay (1/s) and amplitude cap (m) so stacked hits can't
 *  wobble the world. */
const SHAKE_DECAY = 9;
const MAX_SHAKE = 0.09;

export class CombatFeedback {
  private hitstopT = 0;
  private shakeAmp = 0;
  private shakePhase = 0;

  /** A confirmed MELEE hit: freeze scaled by the swing's weight; a killing blow holds
   *  longest. Call once per victim struck — repeats extend, never stack unbounded. */
  meleeHit(weight: "light" | "heavy", killed = false): void {
    const stop = killed ? KILL_HITSTOP : weight === "heavy" ? HEAVY_HITSTOP : LIGHT_HITSTOP;
    this.hitstopT = Math.max(this.hitstopT, stop);
    this.shakeAmp = Math.min(MAX_SHAKE, this.shakeAmp + (weight === "heavy" ? 0.05 : 0.03));
  }

  /** A small camera kick with NO freeze — cannon muzzle thump, confirmed bolt impacts.
   *  Guns stay fluid; only the blade stops time. */
  kick(amp = 0.02): void {
    this.shakeAmp = Math.min(MAX_SHAKE, this.shakeAmp + amp);
  }

  /** The dt to feed avatar.update this frame: crawls during hitstop, real otherwise.
   *  Read-only — the timers decay in applyCamera, which runs once per frame in every
   *  mode (this only runs on foot). */
  avatarDt(dt: number): number {
    return this.hitstopT > 0 ? dt * FREEZE_SCALE : dt;
  }

  /** Decay the timers and add the kick to the finished camera pose. Call exactly once
   *  per render frame, after the frame's camera rig has run. */
  applyCamera(camera: THREE.PerspectiveCamera, dt: number): void {
    this.hitstopT = Math.max(0, this.hitstopT - dt);
    if (this.shakeAmp <= 0) return;
    this.shakePhase += dt * 61; // non-integer rate → a wobble, not a metronome
    _kick
      .set(Math.sin(this.shakePhase * 1.13), Math.cos(this.shakePhase * 0.97), 0)
      .multiplyScalar(this.shakeAmp)
      .applyQuaternion(camera.quaternion);
    camera.position.add(_kick);
    this.shakeAmp *= Math.exp(-SHAKE_DECAY * dt);
    if (this.shakeAmp < 1e-4) this.shakeAmp = 0;
  }
}
const _kick = new THREE.Vector3();

/** The one instance — main.ts and any future combat module share it. */
export const feedback = new CombatFeedback();
